import React from 'react';
import { PerformanceReportExport } from '../../components/PerformanceReportExport';
import { Download, TrendingUp, Award, Timer, CalendarDays, AlertCircle, BarChart3, CheckCircle2, Clock, Send, FileText, Users } from 'lucide-react';

/* ---------- helpers ---------- */
const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  const days = h / 24;
  return `${days.toFixed(1)}d`;
};

const fmtDate = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
};

const rateColor = (r) =>
  r >= 50 ? 'text-green-600' : r >= 25 ? 'text-yellow-600' : 'text-[#475569]';

/* ---------- small subcomponents ---------- */
const KpiCard = ({ icon: Icon, label, value, sub, subClass = 'text-[#5b93d6]', accent = 'bg-[#5b93d6]', testid }) => (
  <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-[#475569] uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-[#0f172a] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={testid}>{value}</p>
        {sub && <p className={`text-xs mt-1 ${subClass}`}>{sub}</p>}
      </div>
      {Icon && (
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  </div>
);

const FunnelViz = ({ funnel }) => {
  const maxCount = Math.max(1, ...funnel.map((f) => f.count));
  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-5">
      <h4 className="text-sm font-semibold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <BarChart3 className="w-4 h-4 text-[#5b93d6]" /> Conversion Funnel
      </h4>
      <div className="space-y-3">
        {funnel.map((f, i) => {
          const widthPct = (f.count / maxCount) * 100;
          const prev = i > 0 ? funnel[i - 1] : null;
          const dropOff = prev && prev.count > 0 ? Math.round(((prev.count - f.count) / prev.count) * 100) : 0;
          const shade = ['bg-[#5b93d6]', 'bg-[#1e3a63]', 'bg-[#0f2444]'][i] || 'bg-[#5b93d6]';
          return (
            <div key={f.stage} data-testid={`funnel-${f.stage.toLowerCase()}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#475569] font-medium">{f.stage}</span>
                <span className="text-[#0f172a] font-semibold">{f.count} <span className="text-[#475569] font-normal">({f.pct}%)</span></span>
              </div>
              <div className="h-6 bg-[#f1f5f9] rounded overflow-hidden">
                <div className={`h-full ${shade} rounded`} style={{ width: `${Math.max(widthPct, 2)}%`, transition: 'width 400ms' }} />
              </div>
              {prev && dropOff > 0 && (
                <p className="text-[10px] text-[#94a3b8] mt-1">↓ {dropOff}% drop-off from {prev.stage}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WeeklyTrend = ({ weeks }) => {
  const width = 480;
  const height = 160;
  const paddingX = 30;
  const paddingY = 20;
  const chartW = width - paddingX * 2;
  const chartH = height - paddingY * 2;
  const maxVal = Math.max(1, ...weeks.flatMap((w) => [w.sent, w.submitted]));
  const barGroupW = chartW / weeks.length;
  const barW = Math.max(4, (barGroupW - 6) / 2);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <CalendarDays className="w-4 h-4 text-[#5b93d6]" /> Last 8 Weeks
        </h4>
        <div className="flex items-center gap-3 text-xs text-[#475569]">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-[#5b93d6]"></span>Sent</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-[#0f2444]"></span>Submitted</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" data-testid="weekly-trend-chart">
        {/* Y-axis grid */}
        {[0, 0.5, 1].map((t) => {
          const y = paddingY + chartH * (1 - t);
          return (
            <g key={t}>
              <line x1={paddingX} y1={y} x2={width - paddingX} y2={y} stroke="#e2e8f0" strokeDasharray="2 3" />
              <text x={paddingX - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{Math.round(maxVal * t)}</text>
            </g>
          );
        })}
        {weeks.map((w, i) => {
          const gx = paddingX + i * barGroupW + 3;
          const sentH = (w.sent / maxVal) * chartH;
          const subH = (w.submitted / maxVal) * chartH;
          return (
            <g key={w.week_start}>
              <rect x={gx} y={paddingY + chartH - sentH} width={barW} height={sentH} fill="#5b93d6" rx="2">
                <title>{`${w.label}: ${w.sent} sent`}</title>
              </rect>
              <rect x={gx + barW + 2} y={paddingY + chartH - subH} width={barW} height={subH} fill="#0f2444" rx="2">
                <title>{`${w.label}: ${w.submitted} submitted`}</title>
              </rect>
              <text x={gx + barW} y={height - 4} textAnchor="middle" fontSize="9" fill="#475569">{w.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const TopPerformer = ({ tp }) => (
  <div className="bg-gradient-to-br from-[#0f2444] via-[#1e3a63] to-[#26518c] text-white rounded-lg shadow-sm p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/80 mb-2">
      <Award className="w-4 h-4" /> Top Performer
    </div>
    {tp ? (
      <>
        <p className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="top-performer-name">{tp.advisor_name || 'Unnamed'}</p>
        <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{tp.submission_rate}%</p>
        <p className="text-xs text-white/80 mt-1">{tp.submitted} of {tp.sent} invites submitted</p>
      </>
    ) : (
      <p className="text-sm text-white/70 mt-2">Need ≥3 invites to rank.</p>
    )}
  </div>
);

const TimingTile = ({ totals }) => (
  <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-5">
    <h4 className="text-sm font-semibold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <Timer className="w-4 h-4 text-[#5b93d6]" /> Time to Completion (median)
    </h4>
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#475569]">Invite → OTP verified</span>
        <span className="font-semibold text-[#0f172a]" data-testid="median-time-verify">{fmtHours(totals.median_hours_to_verify)}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#475569]">Verified → submitted</span>
        <span className="font-semibold text-[#0f172a]" data-testid="median-time-verify-to-submit">{fmtHours(totals.median_hours_verify_to_submit)}</span>
      </div>
      <div className="flex items-center justify-between text-sm border-t border-[#e2e8f0] pt-3">
        <span className="text-[#475569] font-medium">Invite → submitted (end-to-end)</span>
        <span className="font-bold text-[#0f172a]" data-testid="median-time-submit">{fmtHours(totals.median_hours_to_submit)}</span>
      </div>
    </div>
  </div>
);

const StatusBreakdown = ({ totals }) => {
  const buckets = [
    { key: 'submitted', label: 'Submitted', color: 'bg-green-500', count: totals.submitted },
    { key: 'in_progress', label: 'In progress', color: 'bg-blue-500', count: totals.in_progress },
    { key: 'pending', label: 'Pending', color: 'bg-yellow-500', count: totals.pending },
    { key: 'expired', label: 'Expired', color: 'bg-gray-400', count: totals.expired },
    { key: 'revoked', label: 'Revoked', color: 'bg-red-500', count: totals.revoked },
  ];
  const total = buckets.reduce((s, b) => s + b.count, 0) || 1;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-5">
      <h4 className="text-sm font-semibold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <CheckCircle2 className="w-4 h-4 text-[#5b93d6]" /> Invite Status
      </h4>
      <div className="flex h-3 rounded-full overflow-hidden bg-[#f1f5f9] mb-3">
        {buckets.map((b) =>
          b.count > 0 ? (
            <div key={b.key} className={b.color} style={{ width: `${(b.count / total) * 100}%` }} title={`${b.label}: ${b.count}`} />
          ) : null,
        )}
      </div>
      <div className="grid grid-cols-2 gap-y-2 text-xs">
        {buckets.map((b) => (
          <div key={b.key} className="flex items-center gap-2" data-testid={`status-${b.key}`}>
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${b.color}`}></span>
            <span className="text-[#475569]">{b.label}</span>
            <span className="ml-auto font-semibold text-[#0f172a]">{b.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- CSV export ---------- */
const csvCell = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows) => {
  const headers = [
    'Advisor Name', 'Email', 'Role',
    'Sent', 'Sent (30d)', 'Verified', 'Submitted',
    'Verify Rate %', 'Submission Rate %', 'Post-Verify Rate %',
    'In-Progress', 'Pending', 'Expired', 'Revoked',
    'Median Hours to Submit', 'Last Invite',
  ];
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push([
      r.advisor_name || r.advisor_username || '',
      r.advisor_email || '',
      r.role || '',
      r.sent, r.sent_30d, r.verified, r.submitted,
      r.verify_rate, r.submission_rate, r.submission_rate_from_verified,
      r.in_progress, r.pending, r.expired, r.revoked,
      r.median_hours_to_submit ?? '',
      r.last_invite_at ? new Date(r.last_invite_at).toISOString() : '',
    ].map(csvCell).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `advisor-performance-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ---------- main panel ---------- */
export default function AnalyticsPanel({ analytics, onAdvisorClick }) {
  if (!analytics) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-12 text-center text-[#475569]">
        Loading analytics…
      </div>
    );
  }

  const { totals, funnel, weekly_trend: weeks = [], top_performer, per_advisor = [] } = analytics;
  const pendingAttention = totals.pending + totals.in_progress;

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <TrendingUp className="w-5 h-5 text-[#5b93d6]" /> Firm Performance Dashboard
          </h3>
          <p className="text-sm text-[#475569] mt-1">Conversion, velocity, and per-advisor breakdown across all invites.</p>
        </div>
        <PerformanceReportExport />
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Send} label="Invites Sent" value={totals.sent} sub={`${totals.sent_30d} last 30d`} accent="bg-[#5b93d6]" testid="analytics-total-sent" />
        <KpiCard icon={Users} label="OTP Verified" value={totals.verified} sub={`${totals.verify_rate}% verify rate`} accent="bg-[#1e3a63]" />
        <KpiCard icon={FileText} label="Submitted" value={totals.submitted} sub={`${totals.submitted_30d} last 30d`} subClass="text-green-600" accent="bg-[#26518c]" />
        <div className="bg-gradient-to-br from-[#0f2444] via-[#1e3a63] to-[#26518c] text-white rounded-lg shadow-sm p-4">
          <p className="text-xs text-white/80 uppercase tracking-wider">Firm-wide Conversion</p>
          <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="firm-conversion-rate">{totals.submission_rate}%</p>
          <p className="text-xs text-white/70 mt-1">Sent → Submitted · {totals.post_verify_rate}% post-verify</p>
        </div>
      </div>

      {/* Recent Activity row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={CalendarDays} label="Sent · 7 Days" value={totals.sent_7d} accent="bg-[#5b93d6]" />
        <KpiCard icon={CheckCircle2} label="Submitted · 7 Days" value={totals.submitted_7d} subClass="text-green-600" accent="bg-green-600" />
        <KpiCard icon={Clock} label="In Progress" value={totals.in_progress} sub="Verified · not submitted" subClass="text-blue-600" accent="bg-blue-600" />
        <KpiCard icon={AlertCircle} label="Needs Follow-up" value={pendingAttention} sub={`${totals.pending} pending · ${totals.expired} expired`} subClass="text-yellow-700" accent="bg-yellow-500" />
      </div>

      {/* Funnel + Top Performer + Timing */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <FunnelViz funnel={funnel} />
        <div className="grid grid-cols-1 gap-4">
          <TopPerformer tp={top_performer} />
          <TimingTile totals={totals} />
        </div>
        <StatusBreakdown totals={totals} />
      </div>

      {/* Weekly trend */}
      <WeeklyTrend weeks={weeks} />

      {/* Per-Advisor table */}
      <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-4 h-4 text-[#5b93d6]" /> Per-Advisor Performance
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#475569] hidden sm:inline">{onAdvisorClick ? 'Click any row for full profile' : 'Sorted by submissions'}</span>
            <button
              onClick={() => downloadCsv(per_advisor)}
              disabled={per_advisor.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-medium text-[#0f172a] hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="analytics-csv-export-button"
              title="Download per-advisor performance as CSV"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569] uppercase">Advisor</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Sent</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">30d</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Verified</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Submitted</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Submission %</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Post-Verify %</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">In-Progress</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Pending</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Median Time</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#475569] uppercase">Last Invite</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {per_advisor.length ? per_advisor.map((row) => {
                const canDrill = !!onAdvisorClick && row.sent > 0;
                return (
                <tr
                  key={row.advisor_id}
                  className={`${canDrill ? 'cursor-pointer hover:bg-[#f1f5f9]' : 'hover:bg-[#f8fafc]'}`}
                  onClick={() => canDrill && onAdvisorClick(row)}
                  data-testid={`analytics-row-${row.advisor_id}`}
                  title={canDrill ? `View ${row.sent} invite${row.sent === 1 ? '' : 's'} for this advisor` : ''}
                >
                  <td className="px-4 py-3 text-sm text-[#0f172a] font-medium">
                    <span className={canDrill ? 'hover:text-[#5b93d6] hover:underline' : ''}>
                      {row.advisor_name || row.advisor_email || row.advisor_username || '—'}
                    </span>
                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${row.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {row.role?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#0f172a]">{row.sent}</td>
                  <td className="px-4 py-3 text-sm text-right text-[#475569]">{row.sent_30d}</td>
                  <td className="px-4 py-3 text-sm text-right text-[#0f172a]">{row.verified}</td>
                  <td className="px-4 py-3 text-sm text-right text-[#0f172a] font-medium">{row.submitted}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`font-semibold ${rateColor(row.submission_rate)}`}>{row.submission_rate}%</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#0f172a]">{row.submission_rate_from_verified}%</td>
                  <td className="px-4 py-3 text-sm text-right text-[#475569]">{row.in_progress}</td>
                  <td className="px-4 py-3 text-sm text-right text-[#475569]">{row.pending}</td>
                  <td className="px-4 py-3 text-sm text-right text-[#475569]">{fmtHours(row.median_hours_to_submit)}</td>
                  <td className="px-4 py-3 text-sm text-right text-[#475569]">{fmtDate(row.last_invite_at)}</td>
                </tr>
                );
              }) : (
                <tr><td colSpan="11" className="px-6 py-8 text-center text-[#475569]">No data yet. Analytics will populate as invites are sent.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
